import React, { useState, useEffect, useCallback } from 'react';
import { Tag } from 'antd';
import {
  SendOutlined,
  MailOutlined,
  WarningOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Email } from '../../types';
import css from './EmailThread.module.scss';

interface EmailThreadProps {
  emails: Email[];
}

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

interface EmailMessageProps {
  email: Email;
  expanded: boolean;
  onToggle: () => void;
}

const EmailMessage: React.FC<EmailMessageProps> = ({ email, expanded, onToggle }) => {
  const isOutbound = email.direction === 'outbound';

  return (
    <div className={`${css.message} ${isOutbound ? css.outbound : css.inbound}`}>
      <div className={css.messageGutter}>
        <div className={`${css.avatar} ${isOutbound ? css.avatarOutbound : css.avatarInbound}`}>
          {isOutbound ? <SendOutlined /> : <MailOutlined />}
        </div>
        <div className={css.threadLine} />
      </div>

      <div className={css.messageContent}>
        <div className={css.messageHeader} onClick={onToggle}>
          <div className={css.senderRow}>
            <span className={css.sender}>
              {isOutbound ? 'You' : email.sender}
            </span>
            {email.requiresReply && !isOutbound && (
              <Tag
                icon={<WarningOutlined />}
                color="warning"
                className={css.replyBadge}
              >
                Reply needed
              </Tag>
            )}
          </div>
          <div className={css.metaRow}>
            <span className={css.timestamp}>{formatDateTime(email.receivedAt)}</span>
            <button className={css.expandToggle}>
              {expanded ? <UpOutlined /> : <DownOutlined />}
            </button>
          </div>
        </div>

        {expanded && (
          <>
            {email.subject && (
              <div className={css.subject}>{email.subject}</div>
            )}
            <div className={css.body}>{email.body}</div>
          </>
        )}
      </div>
    </div>
  );
};

const EmailThread: React.FC<EmailThreadProps> = ({ emails }) => {
  const buildExpandedState = useCallback((emailList: Email[]) => {
    const state: Record<string, boolean> = {};
    emailList.forEach((email, index) => {
      state[email.id] = index === emailList.length - 1;
    });
    return state;
  }, []);

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(() =>
    buildExpandedState(emails)
  );

  // Reset expanded states when the email list changes (new message arrives, etc.)
  useEffect(() => {
    setExpandedMap(buildExpandedState(emails));
  }, [emails, buildExpandedState]);

  const handleToggle = (emailId: string) => {
    setExpandedMap(prev => ({ ...prev, [emailId]: !prev[emailId] }));
  };

  if (!emails || emails.length === 0) {
    return (
      <div className={css.emptyThread}>
        <MailOutlined className={css.emptyIcon} />
        <p>No email history</p>
      </div>
    );
  }

  return (
    <div className={css.thread}>
      {emails.map((email) => (
        <EmailMessage
          key={email.id}
          email={email}
          expanded={!!expandedMap[email.id]}
          onToggle={() => handleToggle(email.id)}
        />
      ))}
    </div>
  );
};

export default EmailThread;
